/**
 * A training manual, on screen: /guide/cashier, /guide/manager, /guide/setup.
 *
 * Public and static, like the shop guide it hangs off: a trainee at an unpaired
 * till has nobody to sign in as, and a till with no signal still has the words,
 * because they are bundled rather than fetched. The Word download beside the
 * title is built from the same JSON (see ./manuals/index.ts).
 */

import { ArrowLeft, Clock, FileDown, Printer } from "lucide-react";
import { Fragment, type ReactNode } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import { Logo } from "@/components/ui";

import { GuideTable, HeroChip, Note, Steps } from "./blocks";
import { MANUALS, findManual, totalMinutes, type Block, type CalloutKind } from "./manuals";

const CALLOUTS: Record<
  CalloutKind,
  { tone: "green" | "brand" | "amber" | "red" | "gold"; label: string }
> = {
  tip: { tone: "green", label: "Tip" },
  note: { tone: "brand", label: "Good to know" },
  warn: { tone: "amber", label: "Watch out" },
  dont: { tone: "red", label: "Never" },
  why: { tone: "gold", label: "Why it works this way" },
};

export function ManualPage() {
  const { slug } = useParams();
  const entry = findManual(slug);
  if (!entry) return <Navigate to="/guide" replace />;

  const { manual, docx } = entry;
  const minutes = totalMinutes(manual);
  const others = MANUALS.filter((m) => m.slug !== entry.slug);

  return (
    <div data-print="page" className="min-h-screen bg-canvas text-ink-600">
      <header className="bg-navy-900 text-white print:bg-white print:text-ink-900">
        <div className="mx-auto max-w-[1120px] px-4 pb-8 pt-8 sm:px-6 lg:pb-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="inline-flex rounded-[14px] bg-white px-3.5 py-2.5">
              <Logo height={44} label="Jalusi SmartSpaza" />
            </span>
            <Link
              to="/guide"
              className="inline-flex h-touch items-center gap-2 rounded-control px-3 text-sm font-medium text-brand-100 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 print:hidden"
            >
              <ArrowLeft className="size-4" aria-hidden />
              Shop guide
            </Link>
          </div>
          <p className="mt-7 text-sm font-bold uppercase tracking-wider text-gold-500">
            {manual.audience}
          </p>
          <h1 className="mt-2 text-balance text-[clamp(2rem,5vw,3rem)] font-extrabold leading-[1.05] tracking-tight">
            {manual.title}
          </h1>
          <p className="mt-3 max-w-[52ch] text-lg text-brand-100 print:text-ink-600">
            {manual.subtitle}
          </p>
          <ul className="mt-6 flex flex-wrap gap-2 print:hidden">
            <HeroChip dot="bg-gold-500">{manual.modules.length} modules</HeroChip>
            {minutes > 0 && <HeroChip dot="bg-money-500">About {formatMinutes(minutes)}</HeroChip>}
          </ul>
          <div className="mt-6 flex flex-wrap gap-3 print:hidden">
            <a
              href={docx}
              download
              className="inline-flex h-touch items-center gap-2 rounded-control bg-gold-500 px-4 font-semibold text-navy-900 hover:bg-gold-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <FileDown className="size-4" aria-hidden />
              Download for Word
            </a>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex h-touch items-center gap-2 rounded-control border border-navy-600 px-4 font-semibold text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
            >
              <Printer className="size-4" aria-hidden />
              Print
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-[1120px] gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-12 lg:py-12 print:block">
        <nav aria-label="Modules" className="self-start lg:sticky lg:top-6 print:hidden">
          <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-ink-500">
            In this manual
          </p>
          <ol className="grid gap-0.5 sm:grid-cols-2 lg:grid-cols-1">
            {manual.modules.map((m, i) => (
              <li key={m.title}>
                <a
                  href={`#module-${i + 1}`}
                  className="flex gap-2.5 rounded-lg px-2.5 py-1.5 text-[0.92rem] leading-snug hover:bg-brand-50 hover:text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  <span className="min-w-[1.2em] font-numeric text-[0.82rem] text-ink-500">
                    {i + 1}
                  </span>
                  {m.title}
                </a>
              </li>
            ))}
            {manual.reference && (
              <li>
                <a
                  href="#reference"
                  className="flex gap-2.5 rounded-lg px-2.5 py-1.5 text-[0.92rem] leading-snug hover:bg-brand-50 hover:text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  <span className="min-w-[1.2em] font-numeric text-[0.82rem] text-ink-500">?</span>
                  {manual.reference.title}
                </a>
              </li>
            )}
          </ol>
        </nav>

        <main className="grid min-w-0 gap-14 print:block print:space-y-10">
          <section className="grid max-w-[68ch] gap-3 rounded-card border border-line bg-surface p-5">
            <h2 className="text-lg font-bold text-ink-900">In this manual you will learn to</h2>
            <ul className="grid list-disc gap-1.5 pl-5">
              {manual.outcomes.map((o) => (
                <li key={o}>
                  <Rich text={o} />
                </li>
              ))}
            </ul>
          </section>

          {manual.modules.map((m, i) => (
            <section
              key={m.title}
              id={`module-${i + 1}`}
              aria-labelledby={`module-${i + 1}-title`}
              className="grid min-w-0 scroll-mt-6 gap-4 print:block print:space-y-4"
            >
              <div className="flex items-start gap-4">
                <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-brand-500 text-xl font-extrabold text-white">
                  {i + 1}
                </span>
                <div>
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-500">
                    Module {i + 1}
                    {m.minutes ? (
                      <>
                        {" · "}
                        <Clock className="size-3.5" aria-hidden /> {m.minutes} min
                      </>
                    ) : null}
                  </span>
                  <h2
                    id={`module-${i + 1}-title`}
                    className="text-balance text-[1.6rem] font-extrabold leading-tight text-ink-900 sm:text-[1.85rem]"
                  >
                    {m.title}
                  </h2>
                  <p className="mt-1 text-ink-500">
                    <Rich text={m.goal} />
                  </p>
                </div>
              </div>
              <div className="grid max-w-[68ch] gap-4 text-[1.02rem] leading-relaxed print:mt-4 print:block print:max-w-none print:space-y-4">
                {m.blocks.map((block, b) => (
                  <BlockView key={b} block={block} />
                ))}
              </div>
            </section>
          ))}

          {manual.reference && (
            <section
              id="reference"
              className="grid min-w-0 scroll-mt-6 gap-4 print:block print:space-y-4"
            >
              <h2 className="text-[1.6rem] font-extrabold leading-tight text-ink-900 sm:text-[1.85rem]">
                {manual.reference.title}
              </h2>
              <div className="grid max-w-[68ch] gap-4 text-[1.02rem] leading-relaxed print:mt-4 print:block print:max-w-none print:space-y-4">
                {manual.reference.intro && (
                  <p>
                    <Rich text={manual.reference.intro} />
                  </p>
                )}
                <GuideTable
                  head={manual.reference.head}
                  rows={manual.reference.rows.map((row) => row.map((cell) => <Rich text={cell} />))}
                />
                {manual.reference.note && (
                  <div className="grid gap-2 rounded-2xl bg-navy-900 p-6 text-white">
                    <p className="text-brand-100">
                      <Rich text={manual.reference.note} />
                    </p>
                  </div>
                )}
              </div>
            </section>
          )}

          <section className="grid gap-3 print:hidden" aria-labelledby="other-manuals">
            <h2 id="other-manuals" className="text-lg font-bold text-ink-900">
              Other manuals
            </h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to={`/guide/${o.slug}`}
                  className="grid gap-1 rounded-card border border-line bg-surface p-4 hover:border-brand-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  <span className="text-xs font-semibold uppercase tracking-wider text-brand-500">
                    {o.short}
                  </span>
                  <span className="font-bold text-ink-900">{o.manual.title}</span>
                </Link>
              ))}
            </div>
          </section>
        </main>
      </div>

      <footer className="border-t border-line">
        <div className="mx-auto flex max-w-[1120px] flex-wrap justify-between gap-x-6 gap-y-2 px-4 pb-10 pt-6 text-sm text-ink-500 sm:px-6">
          <span>
            {manual.code} · {manual.version}
          </span>
          <span>Jalusi SmartSpaza</span>
        </div>
      </footer>
    </div>
  );
}

function BlockView({ block }: { block: Block }) {
  switch (block.type) {
    case "h2":
      return (
        <h3 className="mt-2 text-xl font-bold text-brand-500">
          <Rich text={block.text} />
        </h3>
      );
    case "h3":
      return (
        <h4 className="mt-1 text-lg font-bold text-ink-900">
          <Rich text={block.text} />
        </h4>
      );
    case "p":
      return (
        <p>
          <Rich text={block.text} />
        </p>
      );
    case "bullets":
      return (
        <ul className="grid list-disc gap-1.5 pl-5">
          {block.items.map((item) => (
            <li key={item}>
              <Rich text={item} />
            </li>
          ))}
        </ul>
      );
    case "steps":
      return (
        <Steps>
          {block.items.map((item) => (
            <Rich key={item} text={item} />
          ))}
        </Steps>
      );
    case "callout": {
      const { tone, label } = CALLOUTS[block.kind];
      return (
        <Note tone={tone} title={block.title ? `${label}: ${block.title}` : label}>
          <BodyLines lines={block.body} />
        </Note>
      );
    }
    case "table":
      return (
        <GuideTable
          head={block.head}
          rows={block.rows.map((row) => row.map((cell) => <Rich text={cell} />))}
        />
      );
    case "checklist":
      return (
        <div className="grid gap-2">
          {block.title && <h4 className="text-lg font-bold text-ink-900">{block.title}</h4>}
          <ul className="grid gap-2">
            {block.items.map((item) => (
              <li
                key={item}
                className="flex gap-3 rounded-card border border-line bg-surface px-4 py-3"
              >
                <span
                  className="mt-1 size-4 shrink-0 rounded border-2 border-brand-500"
                  aria-hidden
                />
                <span>
                  <Rich text={item} />
                </span>
              </li>
            ))}
          </ul>
        </div>
      );
    case "practice":
      return (
        <div className="grid gap-2 rounded-card border-2 border-brand-500 bg-surface p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-brand-500">
            Practice · <span className="normal-case text-ink-900">{block.title}</span>
          </p>
          <ol className="grid list-decimal gap-1.5 pl-5">
            {block.tasks.map((task) => (
              <li key={task}>
                <Rich text={task} />
              </li>
            ))}
          </ol>
        </div>
      );
    case "quiz":
      return (
        <div className="grid gap-2">
          <h3 className="mt-2 text-xl font-bold text-brand-500">Check yourself</h3>
          {block.items.map((item) => (
            <details key={item.q} className="rounded-card border border-line bg-surface px-4 py-3">
              <summary className="cursor-pointer font-medium text-ink-900">
                <Rich text={item.q} />
              </summary>
              <p className="mt-2 text-ink-600">
                <Rich text={item.a} />
              </p>
            </details>
          ))}
        </div>
      );
  }
}

function BodyLines({ lines }: { lines: string[] }) {
  const bullets = lines.filter((l) => l.startsWith("- "));
  if (bullets.length === lines.length) {
    return (
      <ul className="grid list-disc gap-1 pl-5">
        {lines.map((l) => (
          <li key={l}>
            <Rich text={l.slice(2)} />
          </li>
        ))}
      </ul>
    );
  }
  return (
    <>
      {lines.map((l) => (
        <p key={l}>
          <Rich text={l.startsWith("- ") ? l.slice(2) : l} />
        </p>
      ))}
    </>
  );
}

/** `**bold**` and `[[On-screen label]]`, the two marks the manuals use. */
export function Rich({ text }: { text: string }) {
  const parts = text.split(/(\*\*.+?\*\*|\[\[.+?\]\])/g).filter(Boolean);
  return (
    <>
      {parts.map((part, i): ReactNode => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return (
            <strong key={i} className="font-semibold text-ink-900">
              {part.slice(2, -2)}
            </strong>
          );
        }
        if (part.startsWith("[[") && part.endsWith("]]")) {
          return (
            <span key={i} className="font-semibold text-brand-500">
              {part.slice(2, -2)}
            </span>
          );
        }
        return <Fragment key={i}>{part}</Fragment>;
      })}
    </>
  );
}

function formatMinutes(total: number): string {
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (!hours) return `${mins} minutes`;
  return mins ? `${hours} h ${mins} min` : `${hours} hours`;
}
